import React, { useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { StepIndicator } from '../components/StepIndicator';
import { PlusIcon, TypeIcon, MapPinIcon, PhoneIcon, TagIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const STEPS = ['Master Creative', 'Campaign Setup', 'Select Creative', 'Styling Template', 'Variable Content', 'Generate', 'Review & Publish'];
const TEMPLATES = [{
  id: 1,
  name: 'Standard Dealer Template',
  fields: 4,
  lastUsed: '2024-01-15'
}, {
  id: 2,
  name: 'Festival Offer Template',
  fields: 6,
  lastUsed: '2024-01-10'
}, {
  id: 3,
  name: 'Premium Launch Template',
  fields: 5,
  lastUsed: '2024-01-05'
}];
const FIELD_TYPES = [{
  id: 'dealer_name',
  label: 'Dealer Name',
  icon: <TypeIcon size={16} data-id="element-2250" />,
  auto: true
}, {
  id: 'phone',
  label: 'Phone Number',
  icon: <PhoneIcon size={16} data-id="element-2251" />,
  auto: true
}, {
  id: 'address',
  label: 'Address',
  icon: <MapPinIcon size={16} data-id="element-2252" />,
  auto: true
}, {
  id: 'pricing',
  label: 'State Pricing',
  icon: <TagIcon size={16} data-id="element-2253" />,
  auto: false
}, {
  id: 'offer',
  label: 'Offer Text',
  icon: <TagIcon size={16} data-id="element-2254" />,
  auto: false
}, {
  id: 'festival',
  label: 'Festival Text',
  icon: <TypeIcon size={16} data-id="element-2255" />,
  auto: false
}];
export function StylingTemplate() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [templateName, setTemplateName] = useState('');
  const toggleField = (fieldId: string) => {
    setSelectedFields(prev => prev.includes(fieldId) ? prev.filter(id => id !== fieldId) : [...prev, fieldId]);
  };
  const canContinue = mode === 'existing' && selectedTemplate !== null || mode === 'new' && templateName && selectedFields.length > 0;
  return <div className="flex w-full min-h-screen bg-gray-50" data-id="element-2256">
      <Sidebar data-id="element-2257" />
      <main className="flex-1" data-id="element-2258">
        <Header data-id="element-2259" />
        <StepIndicator currentStep={4} steps={STEPS} data-id="element-2260" />
        <div className="px-6 py-8" data-id="element-2261">
          <div className="max-w-5xl mx-auto" data-id="element-2262">
            <h1 className="text-2xl font-semibold text-gray-800 mb-2" data-id="element-2263">
              Step 3: Styling Template
            </h1>
            <p className="text-gray-600 mb-6" data-id="element-2264">
              Create or apply dynamic field templates for dealer customization
            </p>

            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6" data-id="element-2265">
              <div className="flex space-x-4 mb-6" data-id="element-2266">
                <button onClick={() => setMode('existing')} className={`px-6 py-2 rounded-md font-medium transition-colors ${mode === 'existing' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2267">
                  Use Existing Template
                </button>
                <button onClick={() => setMode('new')} className={`px-6 py-2 rounded-md font-medium transition-colors ${mode === 'new' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2268">
                  Create New Template
                </button>
              </div>

              {mode === 'existing' ? <div className="space-y-3" data-id="element-2269">
                  <h3 className="font-medium text-gray-800 mb-4" data-id="element-2270">
                    Saved Templates
                  </h3>
                  {TEMPLATES.map(template => <button key={template.id} onClick={() => setSelectedTemplate(template.id)} className={`w-full p-4 rounded-lg border-2 text-left transition-all ${selectedTemplate === template.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2271">
                      <div className="flex items-center justify-between" data-id="element-2272">
                        <div data-id="element-2273">
                          <p className="font-medium text-gray-800" data-id="element-2274">
                            {template.name}
                          </p>
                          <p className="text-sm text-gray-500 mt-1" data-id="element-2275">
                            {template.fields} dynamic fields • Last used:{' '}
                            {template.lastUsed}
                          </p>
                        </div>
                        {selectedTemplate === template.id && <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center" data-id="element-2276">
                            <svg className="w-4 h-4 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2277">
                              <path d="M5 13l4 4L19 7" data-id="element-2278"></path>
                            </svg>
                          </div>}
                      </div>
                    </button>)}
                </div> : <div className="space-y-6" data-id="element-2279">
                  <div data-id="element-2280">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2281">
                      Template Name
                    </label>
                    <input type="text" value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="Enter template name" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2282" />
                  </div>

                  <div data-id="element-2283">
                    <h3 className="font-medium text-gray-800 mb-4" data-id="element-2284">
                      Add Dynamic Fields
                    </h3>
                    <div className="grid grid-cols-2 gap-3" data-id="element-2285">
                      {FIELD_TYPES.map(field => <button key={field.id} onClick={() => toggleField(field.id)} className={`p-4 rounded-lg border-2 text-left transition-all ${selectedFields.includes(field.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2286">
                          <div className="flex items-center justify-between mb-2" data-id="element-2287">
                            <div className="flex items-center" data-id="element-2288">
                              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center mr-3" data-id="element-2289">
                                {field.icon}
                              </div>
                              <span className="font-medium text-gray-800" data-id="element-2290">
                                {field.label}
                              </span>
                            </div>
                            {selectedFields.includes(field.id) && <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center" data-id="element-2291">
                                <svg className="w-3 h-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2292">
                                  <path d="M5 13l4 4L19 7" data-id="element-2293"></path>
                                </svg>
                              </div>}
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ${field.auto ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-2294">
                            {field.auto ? 'Auto-filled' : 'Manual input'}
                          </span>
                        </button>)}
                    </div>
                  </div>

                  {selectedFields.length > 0 && <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-2295">
                      <h4 className="font-medium text-gray-800 mb-3" data-id="element-2296">
                        Field Configuration
                      </h4>
                      <div className="space-y-3" data-id="element-2297">
                        {selectedFields.map(fieldId => {
                    const field = FIELD_TYPES.find(f => f.id === fieldId);
                    return <div key={fieldId} className="bg-white p-3 rounded-lg border border-gray-200" data-id="element-2298">
                              <p className="font-medium text-sm text-gray-800 mb-2" data-id="element-2299">
                                {field?.label}
                              </p>
                              <div className="grid grid-cols-3 gap-2 text-xs" data-id="element-2300">
                                <div data-id="element-2301">
                                  <label className="text-gray-600" data-id="element-2302">
                                    Font Size
                                  </label>
                                  <input type="number" defaultValue="16" className="w-full px-2 py-1 border border-gray-300 rounded mt-1" data-id="element-2303" />
                                </div>
                                <div data-id="element-2304">
                                  <label className="text-gray-600" data-id="element-2305">Color</label>
                                  <input type="color" defaultValue="#000000" className="w-full h-7 border border-gray-300 rounded mt-1" data-id="element-2306" />
                                </div>
                                <div data-id="element-2307">
                                  <label className="text-gray-600" data-id="element-2308">
                                    Alignment
                                  </label>
                                  <select className="w-full px-2 py-1 border border-gray-300 rounded mt-1" data-id="element-2309">
                                    <option data-id="element-2310">Left</option>
                                    <option data-id="element-2311">Center</option>
                                    <option data-id="element-2312">Right</option>
                                  </select>
                                </div>
                              </div>
                            </div>;
                  })}
                      </div>
                    </div>}
                </div>}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-id="element-2313">
              <h4 className="font-medium text-blue-900 mb-2" data-id="element-2314">
                Dynamic Field Types
              </h4>
              <div className="text-sm text-blue-800 space-y-1" data-id="element-2315">
                <p data-id="element-2316">
                  • <strong data-id="element-2317">Auto-filled:</strong> Dealer Name, Phone, Address,
                  Logo (from database)
                </p>
                <p data-id="element-2318">
                  • <strong data-id="element-2319">Manual:</strong> State Pricing, Offers, Festival
                  Text (configured in next step)
                </p>
              </div>
            </div>

            <div className="flex justify-between" data-id="element-2320">
              <button onClick={() => navigate('/dealerization/step-2')} className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-2321">
                Back
              </button>
              <button onClick={() => navigate('/dealerization/step-4')} disabled={!canContinue} className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-2322">
                Continue to Variable Content
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>;
}